import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import useStyles from './uploadable-file.style'
import { BLUE, IconInline, Image, Paragraph, Video, ProgressBar } from '@omnilert/components'

import ImageViewer from '../image-viewer/image-viewer'


const UPLOAD_STATUS = {
  COMPLETE: 'complete',
  FAILED: 'failed',
  PENDING: 'pending',
  SUCCESSFUL: 'successful',
  UPLOADING: 'uploading'
}

const UploadableFile = (props) => {
  const {
    className,
    file,
    isUploadingFile,
    onBlob,
    onClick,
    onRemove
  } = props

  const { fileId, url, type, parts, mimeType, progress } = file
  const [uploadStatus, setUploadStatus] = useState(UPLOAD_STATUS.PENDING)

  const uploading = isUploadingFile && uploadStatus !== UPLOAD_STATUS.COMPLETE

  const classes = useStyles({ uploading })

  const getClasses = useMemo(() => {
    const baseClasses = [classes.container]

    return classNames(baseClasses, { [className]: !!className })
  }, [
    className,
    classes.container
  ])

  const uploadImage = useCallback(async () => {
    onBlob({ fileId, type, parts, mimeType })
  }, [onBlob, fileId, type, parts, mimeType])

  useEffect(() => {
    let timeout = null

    if (progress > 0 && uploadStatus === UPLOAD_STATUS.PENDING) {
      setUploadStatus(UPLOAD_STATUS.UPLOADING)
    }

    if (progress === 100 && uploadStatus === UPLOAD_STATUS.UPLOADING) {
      setUploadStatus(UPLOAD_STATUS.SUCCESSFUL)
    }

    if (uploadStatus === UPLOAD_STATUS.SUCCESSFUL && !isUploadingFile) {
      timeout = setTimeout(() => {
        setUploadStatus(UPLOAD_STATUS.COMPLETE)
      }, 500)
    }

    return () => clearTimeout(timeout)
  }, [isUploadingFile, progress, setUploadStatus, uploadStatus])

  useEffect(() => {
    if (isUploadingFile) uploadImage()
  }, [uploadImage, isUploadingFile])

  const isImage = type.includes('image')

  const handleOnExpand = () => {
    if (!isImage) {
      onClick(file)
    }
  }

  const handleOnRemove = () => {
    onRemove({ fileId })
  }


  return (
    <section
      key={fileId}
      className={getClasses}
    >
      <button
        aria-label='remove photo'
        className={classes.button}
        onClick={handleOnRemove}
        type='button'
      >
        <IconInline
          className={classes.icon}
          name='close'
          size={30}
        />
      </button>

      {uploading &&
        <div className={classes.progressBarContainer}>
          <ProgressBar
            progress={progress}
            strokeColor={BLUE}
          />
          <Paragraph
            className={classes.progressText}
            color='var(--image-upload__font)'
            size='sm'
          >
            Uploading...
          </Paragraph>
        </div>}

      <div
        className={classes.fileContainer}
        onClick={handleOnExpand}
      >
        {isImage ? (
          <ImageViewer>
            <Image
              className={classes.file}
              src={url}
            />
          </ImageViewer>
        ) : (
          <Video
            className={classes.file}
            controls={false}
            media={file}
          />
        )}
      </div>
    </section>
  )
}

UploadableFile.defaultProps = {
  className: '',
  fileId: '',
  fileUri: '',
  isUploadingFile: false,
  onBlob: () => {},
  onClick: () => {},
  onRemove: () => { },
  progress: 0
}

UploadableFile.propTypes = {
  className: PropTypes.string,
  file: PropTypes.object,
  isUploadingFile: PropTypes.bool,
  onBlob: PropTypes.func,
  onClick: PropTypes.func,
  onRemove: PropTypes.func,
  progress: PropTypes.number
}

export default UploadableFile
